import { Box, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ImageInput from "../../../components/forms/ImageInput";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import RichTextEditor from "../../../components/ui/RichTextEditor";
import Select from "../../../components/ui/Select";
import Text from "../../../components/ui/Text";
import { ROUTES } from "../../../constants/routes";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { validateImage } from "../../../utils/imageUpload";

export const EditGroup = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { groupId } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [groupData, setGroupData] = useState(null);

  const [initialGroupData, setInitialGroupData] = useState({
    name: "",
    country: "",
    city: "",
    description: "",
    // image: null,
  });
  const [formData, setFormData] = useState(initialGroupData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Country and city options
  const countryOptions = [{ value: "Ireland", label: "Ireland" }];

  const cityOptions = [
    { value: "Cork", label: "Cork" },
    { value: "Dublin", label: "Dublin" },
    { value: "Galway", label: "Galway" },
    { value: "Limerick", label: "Limerick" },
  ];

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges =
      formData.name !== initialGroupData.name ||
      formData.country !== initialGroupData.country ||
      formData.city !== initialGroupData.city ||
      formData.description !== initialGroupData.description;

    setHasUnsavedChanges(hasChanges);
  }, [
    formData,
    initialGroupData.name,
    initialGroupData.country,
    initialGroupData.city,
    initialGroupData.description,
  ]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Reset city if country changes
    if (field === "country") {
      setFormData((prev) => ({
        ...prev,
        country: event.target.value,
        city: "", // Reset city when country changes
      }));
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate(`${ROUTES.VIEW_GROUP.replace(":groupId", groupId)}`);
    }
  };

  const handleDiscardChanges = () => {
    setFormData(initialGroupData);
    setShowUnsavedDialog(false);
    navigate(`${ROUTES.VIEW_GROUP.replace(":groupId", groupId)}`);
  };

  const handleContinueEditing = () => {
    setShowUnsavedDialog(false);
  };

  const handleSaveChanges = async () => {
    try {
      setSubmitting(true);
      const response = await apiService.updateGroup(groupId, formData);
      if (response.success) {
        showToast("Group updated successfully", "success");
        navigate(`${ROUTES.VIEW_GROUP.replace(":groupId", groupId)}`);
      } else {
        showToast("Something went wrong", "error");
      }
    } catch (error) {
      showToast(error.error || "Something went wrong", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getGroupData = async () => {
    setLoading(true);
    try {
      const response = await apiService.fetchGroupById(groupId);
      const groupData = response.data;
      const formattedData = {
        name: groupData.name || "",
        country: groupData.country || "",
        city: groupData.city || "",
        description: groupData.description || "",
        // image: groupData.image || null,
      };
      if (response.success) {
        setGroupData(response.data);
        setFormData(formattedData);
        setInitialGroupData(formattedData);
      } else {
        showToast("Something went wrong", "error");
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle image change from ImageInput
  const handleImageChange = async (file) => {
    if (!file) {
      return;
    }

    // Validate the image
    const validation = validateImage(file);
    if (!validation.isValid) {
      showToast(validation.error, "error");
      return;
    }

    try {
      const response = await apiService.uploadGroupImage(groupId, file);
      if (response.success) {
        await getGroupData();
        await showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  useEffect(() => {
    getGroupData();
  }, [groupId]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      {/*Group Details and Content */}
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: "40px" }}>
            <Text variant="pageHeader">Edit group</Text>
          </Box>

          {/* Group Image Section */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, mb: "40px" }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
              }}
            >
              <ImageInput
                value={groupData?.avatar_image_url}
                onChange={handleImageChange}
                size={120}
              />
            </Box>
          </Box>

          {/* Form Fields */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              mb: "40px",
            }}
          >
            {/* Group Name */}
            <Input
              label="Group name"
              value={formData.name}
              onChange={handleInputChange("name")}
              required
            />

            {/* Country and City Row */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Select
                  label="Country"
                  value={formData.country}
                  onChange={handleInputChange("country")}
                  options={countryOptions}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Select
                  label="City"
                  value={formData.city}
                  onChange={handleInputChange("city")}
                  options={cityOptions}
                  placeholder="Select a city"
                  required
                />
              </Box>
            </Box>

            {/* Description */}
            {/* <Input
              label="Description (optional)"
              value={formData.description}
              onChange={handleInputChange("description")}
              multiline
              rows={4}
              placeholder="Tell people about your group..."
            /> */}

            <RichTextEditor
              label="Description (optional)"
              value={formData.description}
              onChange={(html) => {
                handleInputChange("description")({ target: { value: html } });
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          position: { xs: "fixed", md: "static" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "column" },
          gap: 2,
          p: 2,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: { xs: "auto", md: "200px", lg: "272px" },
          bottom: { xs: 0, md: "auto" },
          left: { xs: 0, md: "auto" },
          right: { xs: 0, md: "auto" },
          zIndex: 10,
        }}
      >
        <Button
          variant="primary"
          onClick={handleSaveChanges}
          sx={{
            fontSize: "16px",
            py: 1.5,
          }}
          disabled={
            submitting ||
            !hasUnsavedChanges ||
            !formData.name ||
            !formData.country ||
            !formData.city
          }
        >
          Save changes
        </Button>
        <Button
          variant="secondary"
          onClick={handleBack}
          sx={{
            fontSize: "16px",
            py: 1.5,
          }}
        >
          Cancel
        </Button>
      </Box>
      {/* </Container> */}

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={handleContinueEditing}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              m: 2,
              width: "310px",
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            Unsaved changes
          </Text>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph">
            You have unsaved changes. Do you want to discard them?
          </Text>
        </DialogContent>

        <Box
          sx={{
            p: 3,
            pt: 1,
            gap: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Button
            sx={{ width: "auto" }}
            variant="primary"
            onClick={handleContinueEditing}
          >
            Continue editing
          </Button>
          <Button
            sx={{ width: "auto" }}
            variant="secondary"
            onClick={handleDiscardChanges}
          >
            Discard changes
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};
